import { useState, useEffect } from "react"
import { ArrowRight } from "lucide-react"
import ProfileSidebar from "./ProfileSideBar"
import JobRequirementsEditor from "./JobRequirementsEditor"
import CandidateProfileStep from "./CandidateProfileStep"
import ApplicationFormStep from "./ApplicationFormStep"
import CreateJobFinalStep from "./Createjobfinalstep"

// ── Shared field label ────────────────────────────────────────────────────────
const FieldLabel = ({ children, required }) => (
  <label
    style={{
      display: "block",
      fontSize: 13,
      fontWeight: 600,
      color: "#374151",
      marginBottom: 6,
      letterSpacing: "0.01em",
    }}
  >
    {children}
    {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
  </label>
)

// ── Shared text input ─────────────────────────────────────────────────────────
const TextInput = ({ error, style = {}, ...props }) => (
  <input
    {...props}
    style={{
      width: "100%",
      height: 38,
      padding: "0 12px",
      background: "#fff",
      border: `1px solid ${error ? "#f87171" : "#e5e7eb"}`,
      borderRadius: 6,
      fontSize: 14,
      fontWeight: 500,
      color: "#111827",
      fontFamily: "'DM Sans', sans-serif",
      outline: "none",
      transition: "border-color 0.15s",
      ...style,
    }}
    onFocus={(e) => {
      e.target.style.borderColor = "#6b7280"
      props.onFocus?.(e)
    }}
    onBlur={(e) => {
      e.target.style.borderColor = error ? "#f87171" : "#e5e7eb"
      props.onBlur?.(e)
    }}
  />
)

// ── Shared select dropdown ────────────────────────────────────────────────────
const SelectDropdown = ({
  label,
  required,
  value,
  placeholder,
  options,
  onSelect,
  error,
  dataDropdown,
  isOpen,
  onToggle,
}) => (
  <div style={{ position: "relative" }} data-dropdown={dataDropdown}>
    <FieldLabel required={required}>{label}</FieldLabel>
    <button
      type="button"
      onClick={onToggle}
      style={{
        width: "100%",
        height: 38,
        padding: "0 12px",
        background: "#fff",
        border: `1px solid ${error ? "#f87171" : "#e5e7eb"}`,
        borderRadius: 6,
        fontSize: 14,
        fontWeight: value ? 500 : 400,
        color: value ? "#111827" : "#9ca3af",
        fontFamily: "'DM Sans', sans-serif",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
        outline: "none",
        transition: "border-color 0.15s",
      }}
    >
      <span>{value || placeholder}</span>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#9ca3af"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ transition: "transform 0.15s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>

    {isOpen && (
      <>
        <div style={{ position: "fixed", inset: 0, zIndex: 10 }} onClick={onToggle} />
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            zIndex: 20,
            overflow: "hidden",
          }}
        >
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onSelect(opt)}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "9px 12px",
                fontSize: 14,
                fontWeight: value === opt ? 600 : 400,
                color: value === opt ? "#111827" : "#374151",
                background: value === opt ? "rgb(250,249,245)" : "transparent",
                border: "none",
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                transition: "background 0.1s",
              }}
              onMouseEnter={(e) => {
                if (value !== opt) e.target.style.background = "#f3f4f6"
              }}
              onMouseLeave={(e) => {
                e.target.style.background = value === opt ? "rgb(250,249,245)" : "transparent"
              }}
            >
              {opt}
            </button>
          ))}
        </div>
      </>
    )}
    {error && (
      <p style={{ marginTop: 5, fontSize: 13, color: "#ef4444" }}>{error}</p>
    )}
  </div>
)

// ── Step indicator ────────────────────────────────────────────────────────────
const StepIndicator = ({ currentStep }) => {
  const steps = ["Job Details", "Candidate Profile", "Pre Interview Form", "Final Steps"]
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {steps.map((label, i) => {
        const step = i + 1
        const isActive = step === currentStep
        const isDone = step < currentStep
        return (
          <div key={step} style={{ display: "flex", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 5,
                  border: `1.5px solid ${isActive || isDone ? "#111827" : "#d1d5db"}`,
                  background: isActive || isDone ? "#111827" : "#fff",
                  color: isActive || isDone ? "#fff" : "#9ca3af",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {isDone ? (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  step
                )}
              </div>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? "#111827" : isDone ? "#374151" : "#6b7280",
                  letterSpacing: "-0.01em",
                }}
              >
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                style={{
                  width: 28,
                  height: 1.5,
                  margin: "0 10px",
                  background: isDone ? "#111827" : "#e5e7eb",
                  borderRadius: 1,
                }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Field error ───────────────────────────────────────────────────────────────
const FieldError = ({ error }) =>
  error ? (
    <p style={{ marginTop: 5, fontSize: 13, color: "#ef4444" }}>{error}</p>
  ) : null

// ── Section divider ───────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, marginBottom: 2 }}>
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "#6b7280",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
    <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
  </div>
)

// ── Main component ────────────────────────────────────────────────────────────
const CreateJobForm = ({
  organizationId = "",
  isEditMode = false,
  existingJobDoc = null,
  onCancel,
  onSubmit,
}) => {
  const [activeTab, setActiveTab] = useState("Jobs")
  const [currentStep, setCurrentStep] = useState(1)

  const [formData, setFormData] = useState({
    role: "",
    jobId: "",
    department: "",
    requirements: "",
    rounds: 4,
    location: "",
    salaryRange: { min: "", max: "", currency: "INR" },
    recruiterEmail: "",
    workMode: "Hybrid",
    candidateProfile: {
      noticePeriod: "",
      noticePeriodDealbreaker: false,
      experience: "",
      experienceDealbreaker: false,
    },
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false)
  const [showWorkModeDropdown, setShowWorkModeDropdown] = useState(false)
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false)
  const [createdJobId, setCreatedJobId] = useState(null)

  const departments = ["Engineering", "QA", "Design", "Product", "Marketing", "Sales"]
  const workModes = ["Hybrid", "Remote", "In-office"]
  const currencies = ["USD", "INR", "EUR", "GBP"]

  useEffect(() => {
    if (isEditMode && existingJobDoc) {
      setFormData({
        id: existingJobDoc.id,
        role: existingJobDoc.role || "",
        jobId: existingJobDoc.jobId || "",
        department: existingJobDoc.department || "",
        requirements: existingJobDoc.requirements || "",
        location: existingJobDoc.location || "",
        salaryRange: existingJobDoc.salaryRange || { min: "", max: "", currency: "INR" },
        recruiterEmail: existingJobDoc.recruiterEmail || "",
        workMode: existingJobDoc.workMode || "Hybrid",
        candidateProfile: existingJobDoc.candidateProfile || {
          noticePeriod: "",
          noticePeriodDealbreaker: false,
          experience: "",
          experienceDealbreaker: false,
        },
        applicationForm: existingJobDoc?.applicationForm || { questions: [] },
      })
    } else {
      resetForm()
    }
  }, [isEditMode, existingJobDoc])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-dropdown="department"]')) setShowDepartmentDropdown(false)
      if (!event.target.closest('[data-dropdown="workmode"]')) setShowWorkModeDropdown(false)
      if (!event.target.closest('[data-dropdown="currency"]')) setShowCurrencyDropdown(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const resetForm = () => {
    setFormData({
      role: "",
      jobId: "",
      department: "",
      requirements: "",
      rounds: 4,
      location: "",
      workMode: "Hybrid",
      salaryRange: { min: "", max: "", currency: "INR" },
      recruiterEmail: "",
      candidateProfile: {
        noticePeriod: "",
        noticePeriodDealbreaker: false,
        experience: "",
        experienceDealbreaker: false,
      },
    })
    setErrors({})
    setIsSubmitting(false)
  }

  const stripCommas = (value) => value.toString().replace(/,/g, "")

  const formatWithCommas = (raw) => {
    if (!raw) return ""
    const num = stripCommas(raw)
    if (!/^\d+$/.test(num)) return raw
    const lastThree = num.slice(-3)
    const rest = num.slice(0, -3)
    const formatted = rest
      ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree
      : lastThree
    return formatted
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    if (name.startsWith("salary.")) {
      const field = name.split(".")[1]
      const raw = stripCommas(value)
      if (raw !== "" && !/^\d+$/.test(raw)) return
      setFormData((prev) => ({
        ...prev,
        salaryRange: { ...prev.salaryRange, [field]: raw },
      }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
    if (errors[name] || errors.salaryRange) {
      setErrors((prev) => ({ ...prev, [name]: null, salaryRange: null }))
    }
  }

  const hasTextContent = (htmlContent) => {
    if (!htmlContent) return false
    const tempDiv = document.createElement("div")
    tempDiv.innerHTML = htmlContent
    return (tempDiv.textContent || tempDiv.innerText || "").trim().length > 0
  }

  const validateStep1 = () => {
    const newErrors = {}
    if (!formData.role.trim()) newErrors.role = "Job role is required"
    if (!formData.jobId.trim()) newErrors.jobId = "Job ID is required"
    if (!formData.department.trim()) newErrors.department = "Department is required"
    if (!hasTextContent(formData.requirements)) newErrors.requirements = "Job requirements are required"
    if (!formData.recruiterEmail.trim()) newErrors.recruiterEmail = "Recruiter email is required"
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.recruiterEmail)) newErrors.recruiterEmail = "Valid email required"
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!validateStep1()) return
      setCurrentStep(2)
    } else if (currentStep === 2) {
      setCurrentStep(3)
    } else if (currentStep === 3) {
      handleCreateJob()
    }
  }

  const handleCreateJob = async () => {
    setIsSubmitting(true)
    try {
      const jobId = await onSubmit(formData)
      if (jobId) {
        setCreatedJobId(jobId)
        setCurrentStep(4)
      }
    } catch (e) {
      console.error("Failed to create job")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBackStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1)
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f9fafb",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');
        * { box-sizing: border-box; }
        .cjf-hide-scroll::-webkit-scrollbar { display: none; }
        .cjf-hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        .cjf-salary-input::-webkit-inner-spin-button,
        .cjf-salary-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        .cjf-salary-input { -moz-appearance: textfield; }
        .cjf-input-base:focus { outline: none; border-color: #6b7280 !important; }
        .cjf-select-btn-base:focus { outline: none; }
      `}</style>

      <div style={{ display: "flex" }}>
        {/* Sidebar */}
        <div
          style={{
            borderRight: "1px solid #f3f4f6",
            minHeight: "100vh",
            background: "#fff",
            overflowY: "auto",
          }}
        >
          <ProfileSidebar activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>

        {/* Main */}
        <div
          style={{
            flex: 1,
            marginLeft: 240,
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
          }}
        >
          {/* ── Top bar ── */}
          <div
            style={{
              background: "rgb(250,249,245)",
              padding: "0 28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              position: "sticky",
              top: 0,
              zIndex: 10,
              height: 56,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
              {/* Title */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  paddingRight: 20,
                  borderRight: "1px solid #f3f4f6",
                }}
              >
                <h2
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#111827",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {isEditMode ? "Edit Job" : "Create Job"}
                </h2>
                <span
                  style={{
                    fontSize: 13,
                    color: "#6b7280",
                    fontWeight: 600,
                    background: "rgb(240,239,233)",
                    border: "1px solid #e5e7eb",
                    borderRadius: 5,
                    padding: "2px 8px",
                  }}
                >
                  {currentStep} / 4
                </span>
              </div>

              {currentStep < 4 && <StepIndicator currentStep={currentStep} />}
            </div>

            {onCancel && (
              <button
                onClick={onCancel}
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#6b7280",
                  background: "transparent",
                  border: "1px solid #d1d5db",
                  borderRadius: 7,
                  padding: "6px 14px",
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = "#9ca3af"
                  e.target.style.color = "#111827"
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = "#e5e7eb"
                  e.target.style.color = "#6b7280"
                }}
              >
                {currentStep === 4 ? "Skip" : "Cancel"}
              </button>
            )}
          </div>

          {/* ── Body ── */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {errors.general && (
              <div
                style={{
                  margin: "20px 28px 0",
                  padding: "12px 16px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 8,
                  fontSize: 13,
                  color: "#dc2626",
                }}
              >
                {errors.general}
              </div>
            )}

            {/* ── Step 1 ── */}
            {currentStep === 1 && (
              <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

                {/* LEFT: fields */}
                <div
                  className="cjf-hide-scroll"
                  style={{
                    width: "40%",
                    background: "#fff",
                    borderRight: "1px solid #f3f4f6",
                    overflowY: "auto",
                    height: "calc(100vh - 56px)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Panel header */}
                  <div
                    style={{
                      padding: "20px 24px 16px",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <p style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 3, letterSpacing: "-0.01em" }}>
                      Job Details
                    </p>
                    <p style={{ fontSize: 13, color: "#6b7280", fontWeight: 400 }}>
                      Basic information about the role
                    </p>
                  </div>

                  <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>

                    <SectionLabel>Role</SectionLabel>

                    {/* Position title */}
                    <div>
                      <FieldLabel required>Position Title</FieldLabel>
                      <TextInput
                        type="text"
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        placeholder="e.g. Senior Software Engineer"
                        error={errors.role}
                      />
                      <FieldError error={errors.role} />
                    </div>

                    {/* Job ID + Department */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <FieldLabel required>Job ID</FieldLabel>
                        <TextInput
                          type="text"
                          name="jobId"
                          value={formData.jobId}
                          onChange={handleInputChange}
                          placeholder="ENG-001"
                          error={errors.jobId}
                        />
                        <FieldError error={errors.jobId} />
                      </div>
                      <SelectDropdown
                        label="Department"
                        required
                        value={formData.department}
                        placeholder="Select"
                        options={departments}
                        dataDropdown="department"
                        isOpen={showDepartmentDropdown}
                        onToggle={() => setShowDepartmentDropdown((v) => !v)}
                        error={errors.department}
                        onSelect={(dept) => {
                          setFormData((prev) => ({ ...prev, department: dept }))
                          setShowDepartmentDropdown(false)
                          if (errors.department) setErrors((prev) => ({ ...prev, department: null }))
                        }}
                      />
                    </div>

                    <SectionLabel>Location & Work</SectionLabel>

                    {/* Work Mode + Location */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <SelectDropdown
                        label="Work Mode"
                        value={formData.workMode}
                        placeholder="Select"
                        options={workModes}
                        dataDropdown="workmode"
                        isOpen={showWorkModeDropdown}
                        onToggle={() => setShowWorkModeDropdown((v) => !v)}
                        error={errors.workMode}
                        onSelect={(mode) => {
                          setFormData((prev) => ({
                            ...prev,
                            workMode: mode,
                            location: mode === "Remote" ? "Remote" : "",
                          }))
                          setShowWorkModeDropdown(false)
                          if (errors.workMode) setErrors((prev) => ({ ...prev, workMode: null }))
                        }}
                      />
                      <div>
                        <FieldLabel>Location</FieldLabel>
                        <TextInput
                          type="text"
                          name="location"
                          value={formData.workMode === "Remote" ? "Remote" : formData.location}
                          onChange={handleInputChange}
                          disabled={formData.workMode === "Remote"}
                          placeholder={formData.workMode === "Remote" ? "Remote" : "City"}
                          style={formData.workMode === "Remote"
                            ? { background: "#f9fafb", color: "#9ca3af", cursor: "not-allowed" }
                            : {}
                          }
                        />
                        <FieldError error={errors.location} />
                      </div>
                    </div>

                    {formData.workMode !== "Remote" && (
                      <p style={{ marginTop: -8, fontSize: 13, color: "#6b7280" }}>
                        Separate multiple locations with commas
                      </p>
                    )}

                    <SectionLabel>Compensation</SectionLabel>

                    {/* Salary Range */}
                    <div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                        <FieldLabel>Salary Range</FieldLabel>
                        {/* Currency selector */}
                        <div style={{ position: "relative" }} data-dropdown="currency">
                          <button
                            type="button"
                            onClick={() => setShowCurrencyDropdown((v) => !v)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                              padding: "4px 10px",
                              background: "#f9fafb",
                              border: "1px solid #e5e7eb",
                              borderRadius: 6,
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#374151",
                              cursor: "pointer",
                              fontFamily: "'DM Sans', sans-serif",
                            }}
                          >
                            {formData.salaryRange.currency}
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#9ca3af"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{ transform: showCurrencyDropdown ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>
                          {showCurrencyDropdown && (
                            <>
                              <div style={{ position: "fixed", inset: 0, zIndex: 10 }} onClick={() => setShowCurrencyDropdown(false)} />
                              <div
                                style={{
                                  position: "absolute",
                                  top: "calc(100% + 4px)",
                                  right: 0,
                                  background: "#fff",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 8,
                                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                                  zIndex: 20,
                                  minWidth: 80,
                                  overflow: "hidden",
                                }}
                              >
                                {currencies.map((cur) => (
                                  <button
                                    key={cur}
                                    type="button"
                                    onClick={() => {
                                      setFormData((prev) => ({
                                        ...prev,
                                        salaryRange: { ...prev.salaryRange, currency: cur },
                                      }))
                                      setShowCurrencyDropdown(false)
                                    }}
                                    style={{
                                      width: "100%",
                                      textAlign: "left",
                                      padding: "9px 12px",
                                      fontSize: 13,
                                      fontWeight: formData.salaryRange.currency === cur ? 600 : 500,
                                      color: "#374151",
                                      background: formData.salaryRange.currency === cur ? "rgb(250,249,245)" : "transparent",
                                      border: "none",
                                      cursor: "pointer",
                                      fontFamily: "'DM Sans', sans-serif",
                                    }}
                                  >
                                    {cur}
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Min / Max */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          border: "1px solid #e5e7eb",
                          borderRadius: 6,
                          overflow: "hidden",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", borderRight: "1px solid #f3f4f6" }}>
                          <span
                            style={{
                              padding: "0 10px",
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#9ca3af",
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                              borderRight: "1px solid #f3f4f6",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              whiteSpace: "nowrap",
                              userSelect: "none",
                            }}
                          >
                            Min
                          </span>
                          <input
                            type="text"
                            name="salary.min"
                            value={formatWithCommas(formData.salaryRange.min)}
                            onChange={handleInputChange}
                            placeholder="0"
                            className="cjf-salary-input"
                            style={{
                              flex: 1,
                              height: 40,
                              border: "none",
                              background: "transparent",
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#111827",
                              padding: "0 10px",
                              outline: "none",
                              fontFamily: "'DM Sans', sans-serif",
                            }}
                          />
                        </div>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span
                            style={{
                              padding: "0 10px",
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#9ca3af",
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                              borderRight: "1px solid #f3f4f6",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              whiteSpace: "nowrap",
                              userSelect: "none",
                            }}
                          >
                            Max
                          </span>
                          <input
                            type="text"
                            name="salary.max"
                            value={formatWithCommas(formData.salaryRange.max)}
                            onChange={handleInputChange}
                            placeholder="0"
                            className="cjf-salary-input"
                            style={{
                              flex: 1,
                              height: 40,
                              border: "none",
                              background: "transparent",
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#111827",
                              padding: "0 10px",
                              outline: "none",
                              fontFamily: "'DM Sans', sans-serif",
                            }}
                          />
                        </div>
                      </div>
                      <p style={{ marginTop: 6, fontSize: 13, color: "#6b7280" }}>
                        Annual salary range (optional)
                      </p>
                      {errors.salaryRange && (
                        <p style={{ marginTop: 5, fontSize: 13, color: "#ef4444" }}>{errors.salaryRange}</p>
                      )}
                    </div>

                    {/* Recruiter Email */}
                    <div>
                      <FieldLabel required>Recruiter Email</FieldLabel>
                      <TextInput
                        type="email"
                        name="recruiterEmail"
                        value={formData.recruiterEmail}
                        onChange={handleInputChange}
                        placeholder="recruiter@company.com"
                        error={errors.recruiterEmail}
                      />
                      <FieldError error={errors.recruiterEmail} />
                    </div>

                  </div>
                </div>

                {/* RIGHT: requirements editor */}
                <div
                  style={{
                    flex: 1,
                    background: "#f9fafb",
                    display: "flex",
                    flexDirection: "column",
                    height: "calc(100vh - 56px)",
                    overflow: "hidden",
                  }}
                >
                  {/* Panel header */}
                  <div
                    style={{
                      padding: "20px 28px 16px",
                      background: "#fff",
                      borderBottom: "1px solid #f3f4f6",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <p style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 3, letterSpacing: "-0.01em" }}>
                        Job Requirements
                      </p>
                      <p style={{ fontSize: 13, color: "#6b7280" }}>
                        Describe the role, responsibilities, and what you're looking for
                      </p>
                    </div>
                    {errors.requirements && (
                      <span style={{ fontSize: 13, color: "#ef4444", fontWeight: 500 }}>
                        {errors.requirements}
                      </span>
                    )}
                  </div>

                  {/* Editor */}
                  <div
                    className="cjf-hide-scroll"
                    style={{ flex: 1, overflowY: "auto", padding: "20px 28px" }}
                  >
                    <JobRequirementsEditor
                      key={isEditMode ? "edit" : "create"}
                      value={formData.requirements}
                      onChange={(value) => {
                        setFormData((prev) => ({ ...prev, requirements: value }))
                        if (errors.requirements) setErrors((prev) => ({ ...prev, requirements: null }))
                      }}
                      error={errors.requirements}
                      minHeight={500}
                      maxLength={10000}
                    />
                  </div>

                  {/* Footer */}
                  <div
                    style={{
                      padding: "14px 28px",
                      background: "#fff",
                      borderTop: "1px solid #f3f4f6",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleNextStep}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "9px 20px",
                        background: "#111827",
                        color: "#fff",
                        fontSize: 13,
                        fontWeight: 600,
                        border: "none",
                        borderRadius: 8,
                        cursor: "pointer",
                        fontFamily: "'DM Sans', sans-serif",
                        letterSpacing: "-0.01em",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#27272a")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "#111827")}
                    >
                      Continue to Candidate Profile
                      <ArrowRight size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 2 ── */}
            {currentStep === 2 && (
              <div style={{ padding: "28px 32px" }}>
                <CandidateProfileStep
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                  setErrors={setErrors}
                  onNext={handleNextStep}
                  onBack={handleBackStep}
                  onSubmit={onSubmit}
                />
              </div>
            )}

            {/* ── Step 3 ── */}
            {currentStep === 3 && (
              <div style={{ padding: "28px 32px" }}>
                <ApplicationFormStep
                  formData={formData}
                  setFormData={setFormData}
                  onBack={handleBackStep}
                  onSubmit={handleCreateJob}
                  isSubmitting={isSubmitting}
                />
              </div>
            )}

            {/* ── Step 4 ── */}
            {currentStep === 4 && (
              <div style={{ padding: "28px 32px" }}>
                <CreateJobFinalStep orgId={organizationId} jobId={createdJobId} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreateJobForm